import {
  PurchaseOrder,
  PurchaseOrderProductLineItem,
  UpsertPurchaseOrderProductInventoryUnit,
  UpsertPurchaseOrderProductStock,
} from '../../../clients/quipli-service/purchase-order/purchase-order';
import { useStepper } from '../../../hooks/use-stepper';
import { useTouchedFields } from '../../../hooks/use-touched-fields';
import { useReceivePurchaseOrderInventory } from './use-receive-purchase-order-inventory';
import { usePurchaseOrderApiV1 } from '../../../clients/quipli-service/purchase-order/purchase-order-api-v1';
import { toast } from '../../../util/toasts';
import { ProductType, ProductTrackingType } from '../../../clients/quipli-service/product/product';
import { useCallback, useMemo, useState } from 'react';

// Step type definition for purchase order receiving
export type PurchaseOrderStep = PurchaseOrderQuantityStep | PurchaseOrderUnitStep | PurchaseOrderSummaryStep;

export type PurchaseOrderQuantityStep = {
  type: 'quantity';
  productIndex: number;
  product: PurchaseOrderProductLineItem;
};

export type PurchaseOrderUnitStep = {
  type: 'unit';
  productIndex: number;
  unitIndex: number;
  product: PurchaseOrderProductLineItem;
};

export type PurchaseOrderSummaryStep = {
  type: 'summary';
};

// Helper function to get step title
const getStepTitle = (step: PurchaseOrderStep) => {
  if (step.type === 'quantity') {
    return `Configure Quantities`;
  }

  if (step.type === 'unit') {
    return `Configure Unit`;
  }

  if (step.type === 'summary') {
    return `Review & Receive`;
  }
};

// Main component hook that orchestrates everything
export const useReceivePurchaseOrderWorkflow = (
  purchaseOrder: PurchaseOrder,
  onCancel: () => void,
  onComplete: () => void,
) => {
  const receivableProducts = purchaseOrder.products.filter((pop) => pop.product.type !== ProductType.Custom);

  // Create steps
  const steps = useMemo(() => {
    const allSteps: PurchaseOrderStep[] = [];

    receivableProducts.forEach((product, productIndex) => {
      if (product.product.trackingType === ProductTrackingType.Quantity) {
        allSteps.push({
          type: 'quantity',
          productIndex,
          product,
        });
      } else if (product.product.trackingType === ProductTrackingType.SerialUnits) {
        // Add a step for each unit
        for (let unitIndex = 0; unitIndex < product.quantity; unitIndex++) {
          allSteps.push({
            type: 'unit',
            productIndex,
            unitIndex,
            product,
          });
        }
      }
    });

    // Add summary step
    allSteps.push({
      type: 'summary',
    });

    return allSteps;
  }, [receivableProducts]);

  const { markFieldAsTouched, isFieldTouched, clearTouchedFields } = useTouchedFields();
  const { currentStep, currentStepIndex, isFirstStep, isLastStep, nextStep, previousStep, markStepAsTouched } =
    useStepper<PurchaseOrderStep>(steps);

  const isSummaryStep = currentStep.type === 'summary';

  const {
    stockUpdates,
    unitUpdates,
    updateStock,
    updateUnits,
    currentStock,
    currentUnitUpdate,
    isCurrentStepValid,
    canReceive,
    currentStepErrors,
  } = useReceivePurchaseOrderInventory(receivableProducts, currentStep);

  // Receive operation logic (moved inline since it's only used here)
  const { receivePurchaseOrder } = usePurchaseOrderApiV1();
  const [isReceiving, setIsReceiving] = useState<boolean>(false);

  // Receive purchase order
  const doReceive = useCallback(async () => {
    setIsReceiving(true);
    try {
      await receivePurchaseOrder(purchaseOrder.id, { products: [...stockUpdates, ...unitUpdates] });
      onComplete();
    } catch (e: any) {
      toast(e.message || 'Failed to receive purchase order', 'error');
      setIsReceiving(false);
    }
  }, [receivePurchaseOrder, purchaseOrder.id, stockUpdates, unitUpdates, onComplete]);

  // Handle next step
  const handleNext = useCallback(() => {
    nextStep();
    clearTouchedFields();
  }, [nextStep, clearTouchedFields]);

  // Handle stock update
  const handleStockUpdate = useCallback(
    (update: UpsertPurchaseOrderProductStock) => {
      markStepAsTouched();
      if (currentStep.type === 'quantity') {
        updateStock(update);
      }
    },
    [markStepAsTouched, updateStock, currentStep],
  );

  // Handle unit update
  const handleUnitUpdate = useCallback(
    (units: UpsertPurchaseOrderProductInventoryUnit[]) => {
      markStepAsTouched();
      if (currentStep.type === 'unit') {
        updateUnits(currentStep.product.id, units);
      }
    },
    [markStepAsTouched, updateUnits, currentStep],
  );

  return {
    // Step management
    currentStep,
    currentStepIndex,
    isFirstStep,
    isLastStep,
    isSummaryStep,
    steps,

    // Data
    currentStock,
    currentUnitUpdate,
    receivableProducts,
    stockUpdates,
    unitUpdates,

    // Validation
    isCurrentStepValid,
    canReceive,
    currentStepErrors,

    // Actions
    handleNext,
    handlePrevious: previousStep,
    handleStockUpdate,
    handleUnitUpdate,
    handleReceive: doReceive,

    // State
    isReceiving,

    // Form validation
    markFieldAsTouched,
    isFieldTouched,

    // Navigation
    onCancel,

    // UI helpers
    getStepTitle,
  };
};
